import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { isAuthenticated, verifyEmail } from "../../shared/api/auth";
import { meQueryKeys } from "../../shared/api/platform";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

type State =
  | { kind: "verifying" }
  | { kind: "ok" }
  | { kind: "error"; message: string };

/**
 * Landing page for the activation link (`/verify-email?token=…`). Confirms the e-mail via the
 * public endpoint, then clears the "waiting" banner by invalidating the cached /me. Works whether
 * the user opens the link in the same tab (logged in) or a fresh browser (logged out).
 */
export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState<State>({ kind: "verifying" });
  const queryClient = useQueryClient();
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // StrictMode double-invoke guard — token is single-use.
    ran.current = true;
    if (!token) {
      setState({ kind: "error", message: "Liên kết không hợp lệ (thiếu mã)." });
      return;
    }
    verifyEmail(token)
      .then(() => {
        setState({ kind: "ok" });
        queryClient.invalidateQueries({ queryKey: meQueryKeys.self() });
      })
      .catch((err: unknown) => {
        setState({
          kind: "error",
          message:
            err instanceof Error
              ? err.message
              : "Liên kết đã hết hạn hoặc không hợp lệ.",
        });
      });
  }, [token, queryClient]);

  const loggedIn = isAuthenticated();

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4 font-sans">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <div className="inline-flex items-center gap-2 text-3xl font-extrabold tracking-tight text-primary">
            <span aria-hidden="true">✚</span> Addy HIS
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Hệ thống quản lý phòng khám
          </p>
        </div>

        <Card>
          {state.kind === "verifying" && (
            <CardHeader>
              <CardTitle className="text-2xl">Đang kích hoạt…</CardTitle>
              <CardDescription>Vui lòng chờ trong giây lát.</CardDescription>
            </CardHeader>
          )}

          {state.kind === "ok" && (
            <>
              <CardHeader>
                <CardTitle className="text-2xl">Kích hoạt thành công ✅</CardTitle>
                <CardDescription>
                  Email của bạn đã được xác nhận. Cảm ơn bạn!
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button asChild className="w-full">
                  <Link to={loggedIn ? "/dashboard" : "/login"}>
                    {loggedIn ? "Vào hệ thống" : "Đăng nhập"}
                  </Link>
                </Button>
              </CardContent>
            </>
          )}

          {state.kind === "error" && (
            <>
              <CardHeader>
                <CardTitle className="text-2xl">Không kích hoạt được</CardTitle>
                <CardDescription>{state.message}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-2 rounded-md bg-muted p-3 text-sm text-muted-foreground">
                  <RefreshCw
                    className="mt-0.5 size-4 shrink-0 text-primary"
                    aria-hidden="true"
                  />
                  <div>
                    Đăng nhập vào hệ thống rồi bấm “Gửi lại email kích hoạt” trên
                    thanh thông báo để nhận liên kết mới.
                  </div>
                </div>
                <Button asChild className="w-full">
                  <Link to={loggedIn ? "/dashboard" : "/login"}>
                    {loggedIn ? "Vào hệ thống" : "Đăng nhập"}
                  </Link>
                </Button>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
